import {resolvePointColor} from '../PanelMediaBrowser/common';
/**
 * Types-only from the dense draw module so this adapter stays free of a static
 * Babylon runtime dependency (dense draw is loaded via dynamic `import()`).
 */
import type {
  BabylonPointCloud,
  RenderFullscreen,
  RenderScreenshot,
} from './babylonDenseDraw';
import {POINT_CLOUD_DRAW} from './pointCloudDrawConfig';
import {
  type PointCloudCardRenderRequest,
  type PointCloudFullscreenRenderRequest,
  type PointCloudRenderer,
  type PointCloudRgb,
  type PointCloudScene,
} from './types';

type BabylonDenseDraw = typeof import('./babylonDenseDraw');

let babylonDenseDrawPromise: Promise<BabylonDenseDraw> | undefined;

function loadBabylonDenseDraw(): Promise<BabylonDenseDraw> {
  babylonDenseDrawPromise ??= import('./babylonDenseDraw');
  return babylonDenseDrawPromise;
}

/**
 * Only Babylon-specific conversion: shared scene → `renderJsonPoints` cloud.
 * Does not truncate; preprocess already did. Resolves category/default colors
 * here so draw only packs RGB buffers. Box edges stay as position tuples —
 * Vector3 conversion happens inside the lazy dense-draw module.
 */
export function sceneToBabylonCloud(
  scene: PointCloudScene,
  pointColor?: PointCloudRgb
): BabylonPointCloud {
  return {
    points: scene.points.map(point => ({
      position: point.position,
      category: point.category,
      color: pointColor ?? resolvePointColor(point),
    })),
    vectors: scene.vectors.map(vector => ({
      start: vector.start,
      end: vector.end,
      color: vector.color ?? POINT_CLOUD_DRAW.defaultVectorColor,
    })),
    boxes: scene.boxes,
  };
}

/**
 * Babylon adapter for {@link PointCloudRenderer}. Truncation and filtering
 * stay in preprocess; this only converts the shared scene and draws.
 */
export const babylonPointCloudRenderer: PointCloudRenderer = {
  id: 'babylon',

  async renderCard(request: PointCloudCardRenderRequest) {
    const babylonDraw = await loadBabylonDenseDraw();
    const pointCloud = sceneToBabylonCloud(request.scene, request.pointColor);
    const result = babylonDraw.renderJsonPoints<RenderScreenshot>(
      pointCloud,
      {
        fullscreen: false,
        width: request.width,
        height: request.height,
      },
      request.cameraControl,
      request.backgroundColor,
      request.useRightHandedSystem
    );
    const imageUrl = await babylonDraw.renderScreenshot(result);
    return {imageUrl, cleanup: result.cleanup};
  },

  async mountFullscreen(request: PointCloudFullscreenRenderRequest) {
    const babylonDraw = await loadBabylonDenseDraw();
    const pointCloud = sceneToBabylonCloud(request.scene, request.pointColor);
    const result = babylonDraw.renderJsonPoints<RenderFullscreen>(
      pointCloud,
      {
        domElement: request.domElement,
        fullscreen: true,
      },
      // Matches legacy PointCloud.tsx: fullscreen does not pass cameraControl.
      undefined,
      request.backgroundColor,
      request.useRightHandedSystem
    );
    await babylonDraw.renderFullscreen(result);
    return {cleanup: result.cleanup};
  },
};
